"use client"

import React from "react"

/**
 * HideContextの型定義
 */
type HideContextValue = {
  register: (setter: (visible: boolean) => void) => void
  unregister: (setter: (visible: boolean) => void) => void
  resetAll: () => void
}

/**
 * Context本体
 */
const HideContext = React.createContext<HideContextValue | undefined>(undefined)

/**
 * このProviderが無いと <Hide> や <HideResetButton> が機能しない
 */
export function HideProvider({ children }: { children: React.ReactNode }) {
  const settersRef = React.useRef<Array<(visible: boolean) => void>>([])

  // 全部隠す
  const resetAll = React.useCallback(() => {
    for (const setVisible of settersRef.current) {
      setVisible(false)
    }
  }, [])

  // <Hide> コンポーネントがマウント時に setRevealed を登録し、アンマウント時に解除
  const register = React.useCallback((setter: (visible: boolean) => void) => {
    settersRef.current.push(setter)
  }, [])

  const unregister = React.useCallback((setter: (visible: boolean) => void) => {
    settersRef.current = settersRef.current.filter((s) => s !== setter)
  }, [])

  const value: HideContextValue = {
    register,
    unregister,
    resetAll,
  }

  return <HideContext.Provider value={value}>{children}</HideContext.Provider>
}

/**
 * HideContextを利用するためのカスタムフック
 */
export function useHideContext() {
  const context = React.useContext(HideContext)
  if (!context) {
    throw new Error("useHideContext must be used within a <HideProvider>")
  }
  return context
}
